import { router } from 'expo-router'
import type { ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { useWizardStore } from '@/src/features/settings/stores/useWizardStore'
import { useThemeTagsQuery } from '@/src/features/tag/services/useThemeTagsQuery'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import type { TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { useBackHandlePress } from '@/src/general/hooks/useBackHandlePress'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { WizardContentLayout } from '../../layouts/WizardContentLayout'
import { TagsButtonPlaceholders } from './TagsButtonPlaceholders'

export const ThemeStep = ({ source }: TSourceParam): ReactNode => {
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const { handleBackPress } = useBackHandlePress(routes.communities())
  const tags = useWizardStore((state) => state.tags)
  const toggleTag = useWizardStore((state) => state.toggleTag)
  const resetWizard = useWizardStore((state) => state.resetWizard)
  const { data: me } = useMeQuery()
  const { isLoading, data: themeTags, error } = useThemeTagsQuery({ enabled: !!me?.lang })
  const { lessThan } = useDimensions()

  const handleCancel = () => {
    resetWizard()
    handleBackPress()
  }

  return (
    <WizardContentLayout
      title={t('user.wizard.what-themes')}
      previousButtonProps={
        source === TRACKING_SOURCE.HOME_PAGE
          ? {
              onPress: () => router.navigate(routes.communities()),
              children: t('word.skip'),
            }
          : {
              onPress: handleCancel,
              children: t('word.close'),
            }
      }
      testID={TEST_ID.ME.WIZARD.THEME}
    >
      <View className="flex h-full w-full flex-row flex-wrap justify-center gap-3">
        {!themeTags && error && <ErrorMessage error={error} defaultErrorKey="common.error.unknown" />}
        {!themeTags && isLoading && <TagsButtonPlaceholders />}
        {themeTags?.map(({ id, name, emoji }) => (
          <Button
            key={id}
            variant={tags.includes(id) ? 'solid' : 'faded'}
            colors="primary"
            onPress={() => toggleTag(id)}
            size={lessThan.xm ? 'sm' : 'md'}
          >
            {emoji && `${emoji} `}
            {name}
          </Button>
        ))}
      </View>
    </WizardContentLayout>
  )
}
